import { http } from './http';
import { normUsage, type Usage } from './types';

export const accountApi = {
  /**
   * The controller returns the summary verbatim, with no envelope — and `null` as the whole body
   * when the account has no entitlement context. That is not "unlimited" and not "out of quota",
   * it is "unknown", so it stays null and every caller falls through to letting the server decide.
   */
  usage: (signal?: AbortSignal): Promise<Usage | null> =>
    http.get<Record<string, unknown> | null>('/account/usage', { signal }).then((j) => (j ? normUsage(j) : null)),
  registerSubscription: (rcAppUserId: string) => http.post<unknown>('/account/subscription/register', { rc_app_user_id: rcAppUserId }),

  /** Register this browser's FCM token so the backend pushes factura_status / certificate_validation to it. */
  registerDevice: (token: string, deviceName: string) => http.post<unknown>('/account/devices', { token, platform: 'web', device_name: deviceName }),
  /** Best-effort removal (shape of DELETE not confirmed by the mobile client). */
  unregisterDevice: async (token: string) => {
    try {
      await http.delete<unknown>(`/account/devices/${encodeURIComponent(token)}`);
    } catch {
      try {
        await http.request('/account/devices', { method: 'DELETE', body: { token } });
      } catch {
        /* ignore */
      }
    }
  },

  /**
   * Soft-deletes the account and everything attached to it: emisor, establecimientos, certificado,
   * clientes, productos and comprobantes. After it returns, every token is invalid and signing in
   * again with the same email, Google or Apple identity starts a brand-new account.
   *
   * It also has to release the RUC so a fresh account can upload a .p12 for it — otherwise the
   * emisor validation lock outlives the account and the user can never re-onboard.
   *
   * Required by Apple's guideline 5.1.1(v) for any app that offers account creation, and by
   * Google Play's Data safety declaration.
   *
   * No body and no server-side confirmation: the bearer token is the whole authorisation. The
   * typed "ELIMINAR" in the dialog is friction for the user, not a check the server makes — so
   * nothing but the UI stands between a stray call and a deleted account.
   *
   * Idempotent in practice: a second call cannot authenticate, because the first one invalidated
   * the token.
   */
  remove: () => http.delete<{ message?: string }>('/account'),
};
